import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form } from '@formio/react';
import type { FormSource, Submission } from '@formio/react/lib/components/Form';
import { Button, AlertModal } from '@components/ui';
import Modal from '@/components/ui/Modal/Modal';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import { getFormConfigs, deleteFormConfig, getFormConfigById } from '@/services/formService';
import { patchFileComponents } from '@/lib/formioFileProvider';
import { useNotificationStore } from '@stores/notificationStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { FormConfigSummary } from '@/types/form';
import type { FilterFieldConfig, ListFilterPayload } from '@/types/filters';
import { WorkpayApiError } from '@lib/apiEnvelope';
import PlusIcon from '@assets/icons/PlusIcon';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import FormsEmptyIcon from '@assets/icons/FormsEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/form/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'createdAt', sortDir: 'desc' }),
  component: FormListPage,
});

function StatusBadge({ status }: { status: FormConfigSummary['status'] }) {
  const { t } = useTranslation();
  const className = status === 'ACTIVE' ? styles.badgePublished : styles.badgeDraft;
  const label = status === 'ACTIVE' ? t('K_STATUS_ACTIVE', 'ACTIVE') : t('K_STATUS_DRAFT', 'DRAFT');
  return <span className={`${styles.badge} ${className}`}>{label}</span>;
}

function FormListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const [deactivateItem, setDeactivateItem] = useState<FormConfigSummary | null>(null);
  const [previewItem, setPreviewItem] = useState<FormConfigSummary | null>(null);
  const audit = useAuditTrigger();
  const auditOpen = audit.open;

  // Lazy-fetch the full schema only when the user opens the preview modal — the
  // list endpoint omits the `formConfig` blob to keep page payloads small.
  const { data: previewConfig, isLoading: previewLoading } = useQuery({
    queryKey: ['formConfig', previewItem?.id],
    queryFn: () => getFormConfigById(previewItem!.id),
    enabled: !!previewItem,
    staleTime: Infinity,
  });

  // Mirror FormAddPage.handlePreview: deep-clone + patchFileComponents so the
  // preview uploads (if any) hit the same file-management-service that the
  // builder uses. Memoised so we don't re-clone on every render.
  const previewSchema = useMemo<FormSource | null>(() => {
    if (!previewConfig?.formConfig) return null;
    const cloned = JSON.parse(JSON.stringify(previewConfig.formConfig)) as Record<string, unknown>;
    patchFileComponents(cloned);
    return cloned as FormSource;
  }, [previewConfig]);

  const handleClosePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const handlePreviewSubmit = useCallback((submission: Submission) => {
    // Match FormAddPage: preview submissions are logged for the author, never persisted.
    console.log('=== Preview Submission ===');
    console.log(JSON.stringify(submission, null, 2));
  }, []);

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Filter fields ordered to match table columns: formName, category, status, updatedAt, createdAt
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'search',
        label: t('K_FILTER_FORM_NAME', 'Form Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_FORM_NAME', 'Search form name...'),
      },
      {
        fieldKey: 'category',
        label: t('K_FILTER_CATEGORY', 'Category'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_CATEGORY', 'Search category...'),
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_DRAFT', 'Draft')), value: 'DRAFT' },
        ],
      },
      {
        fieldKey: 'updatedAt',
        label: t('K_FILTER_UPDATED', 'Updated Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
      {
        fieldKey: 'createdAt',
        label: t('K_FILTER_CREATED', 'Created Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
    ],
    [t],
  );

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo: '/w/form',
    filtersParam,
    fields: filterFields,
  });

  const handleCreateForm = useCallback(() => {
    void navigate({ to: '/w/form/add' });
  }, [navigate]);

  const handleEdit = useCallback(
    (form: FormConfigSummary) => {
      void navigate({ to: '/w/form/add', search: { id: form.id } });
    },
    [navigate],
  );

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deleteFormConfig(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['formConfigs'] });
      setDeactivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setDeactivateItem(null);
    },
  });

  const columns = useMemo<DataTableColumn<FormConfigSummary>[]>(
    () => [
      {
        id: 'formName',
        header: t('K_COLUMN_FORM_NAME', 'Form Name'),
        sortable: true,
        sortKey: 'formName',
        width: '200px',
        cell: (form) => <span className={styles.cellPrimary} title={form.formName}>{form.formName}</span>,
      },
      {
        id: 'category',
        header: t('K_COLUMN_CATEGORY', 'Category'),
        sortable: true,
        sortKey: 'category',
        cell: (form) => form.category || '-',
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (form) => <StatusBadge status={form.status} />,
      },
      {
        id: 'updatedAt',
        header: t('K_COLUMN_LAST_UPDATED', 'Last Updated'),
        sortable: true,
        sortKey: 'updatedAt',
        cell: (form) => (
          <span className={styles.cellSecondary}>
            {new Date(form.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (form) => (
          <span className={styles.cellSecondary}>
            {new Date(form.createdAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [t],
  );

  const rowActions = useCallback((form: FormConfigSummary) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('FORMS_VIEW')) {
      actions.push({ id: 'preview', label: t('K_BUTTON_PREVIEW', 'Preview'), onClick: () => setPreviewItem(form) });
    }
    if (hasPermission('FORMS_UPDATE') && form.status !== 'INACTIVE') {
      actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => handleEdit(form) });
    }
    if (hasPermission('FORMS_DELETE') && form.status === 'ACTIVE') {
      actions.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setDeactivateItem(form) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(form.id, form.formName || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, handleEdit, hasPermission, auditOpen]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  // Build API payload: `search` is a top-level body field, not inside `filters`
  const apiPayload = useMemo<ListFilterPayload | undefined>(() => {
    const searchVal = typeof filters.search === 'string' && filters.search ? filters.search : undefined;

    let apiFilters: Record<string, unknown> | undefined;
    if (filterPayload?.filters) {
      const { search: _, ...rest } = filterPayload.filters;
      if (Object.keys(rest).length > 0) apiFilters = rest;
    }

    if (!searchVal && !apiFilters) return undefined;
    return {
      ...(apiFilters ? { filters: apiFilters } : {}),
      ...(searchVal ? { search: searchVal } : {}),
    };
  }, [filterPayload, filters.search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['formConfigs', { page, size, sort: sortString, filterPayload: apiPayload }],
    queryFn: () => getFormConfigs({ page, size, sort: sortString }, apiPayload),
  });

  const forms = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/form',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/form',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/form',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const getRowKey = useCallback((form: FormConfigSummary) => form.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <FormsEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_FORMS', 'No forms yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_FORM', 'Get started by creating your first custom form template.')}
          </p>
          {hasPermission('FORMS_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreateForm}>
              {t('K_BUTTON_CREATE_FORM', 'Create form')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreateForm, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_FORMS', 'Forms')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_FORMS', 'Build and manage custom form templates')}</p>
        </div>

        {hasPermission('FORMS_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreateForm}>
            {t('K_BUTTON_CREATE_FORM', 'Create form')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={forms}
        rowKey={getRowKey}
        loading={isLoading}
        loadingText={t('K_LOADING_FORMS', 'Loading forms...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Forms list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_FORM', 'Deactivate Form')}
        description={t('K_CONFIRM_DEACTIVATE_FORM', 'Are you sure you want to deactivate "{name}"? The form will no longer be active.').replace('{name}', deactivateItem?.formName ?? '')}
        isOpen={deactivateItem !== null}
        onClose={() => setDeactivateItem(null)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => {
          if (deactivateItem) deactivateMutation.mutate(deactivateItem.id);
        }}
        loading={deactivateMutation.isPending}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.FORM_CONFIG, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />

      {/* Preview Modal — mirrors FormAddPage's preview affordance. Lazy-fetches
          the full schema on open and renders the actual Formio form so authors
          can validate the rendered output without entering edit mode. */}
      <Modal
        isOpen={previewItem !== null}
        onClose={handleClosePreview}
        title={t('K_MODAL_PREVIEW_FORM', 'Preview Form')}
        size="lg"
      >
        {previewLoading ? (
          <div className={styles.previewLoading}>
            {t('K_LOADING_FORM_PREVIEW', 'Loading form preview...')}
          </div>
        ) : previewSchema ? (
          <div className={styles.previewWrap}>
            <Form src={previewSchema} onSubmit={handlePreviewSubmit} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
